import React, { useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import useTrip from "@/stores/tripBuilder/useTrip";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/config/axiosConfig";
import { formatImageUrl } from "@/lib/general";
import { useToast } from "@/hooks/use-toast";
import { TripInfo } from "@/types/trip";
import { AIService } from "@/services/chatService";
import {
  GlobeAmericasIcon,
  LockClosedIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import useAuthStore from "@/stores/user/authStore";
import { useRateLimitStore } from "@/stores/rateLimitStore";
import { Loader2 } from "lucide-react";
import LazyLoad from "../image/LazyLoad";

interface UnsplashPhoto {
  id: string;
  url: string;
  alt?: string;
}

const getSuggestedImages = async (query: string) => {
  const response = await api.content.get(`/unsplash?query=${query}`);
  return response.data;
};

const EditTripInfo = React.memo(() => {
  const tripData = useTrip((state) => state.tripData);
  const updateTripData = useTrip((state) => state.updateTripData);
  const tripInfo = useTrip((state) => state.tripInfo);
  const setTripInfo = useTrip((state) => state.setTripInfo);
  const [name, setName] = useState(tripData?.name || "");
  const anonymousUser = useAuthStore((state) => state.anonymousUser);
  const setRateLimit = useRateLimitStore((state) => state.setRateLimit);
  const [description, setDescription] = useState(tripInfo?.description || "");
  const [tags, setTags] = useState<string[]>(tripInfo?.tags || []);
  const [privacy, setPrivacy] = useState<"public" | "private" | "friends">(
    tripInfo?.privacy || "private"
  );
  const [unsplashPhotos, setUnsplashPhotos] = useState<UnsplashPhoto[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>(
    tripInfo?.images || []
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const MAX_PHOTOS = 5;
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!tripData) return;

      const tripInfo: TripInfo = {
        id: tripData.id,
        description,
        tags,
        privacy: privacy as "public" | "private" | "friends",
        images: selectedPhotos,
      };

      const previousTripData = { ...tripInfo };
      setTripInfo({
        id: tripData.id,
        description,
        tags,
        privacy: privacy as "public" | "private" | "friends",
        images: selectedPhotos,
      });

      updateTripData({
        ...tripData,
        name,
      });

      setIsSaving(true);
      try {
        await api.trips.post(`/${tripData.id}/info`, tripInfo);
        setIsOpen(false);

        toast({
          title: "Changes saved",
          description: "Your trip details have been updated successfully.",
          duration: 3000,
        });
      } catch (error) {
        setTripInfo({
          id: tripData.id,
          description: previousTripData.description,
          tags: previousTripData.tags,
          privacy: previousTripData.privacy,
          images: previousTripData.images,
        });

        toast({
          title: "Error saving changes",
          description: "Please try again later.",
          duration: 4000,
        });

        console.error("Error updating trip info:", error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      tripData,
      description,
      tags,
      privacy,
      selectedPhotos,
      name,
      setTripInfo,
      updateTripData,
      toast,
    ]
  );

  const handleTagInput = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && tagInput.trim()) {
        e.preventDefault();
        setTags((prevTags) => [...prevTags, tagInput.trim()]);
        setTagInput("");
      }
    },
    [tagInput]
  );

  const searchUnsplash = useCallback(
    async (query: string) => {
      if (!query.trim()) return;

      setIsSearching(true);
      try {
        const response = await getSuggestedImages(query);
        setUnsplashPhotos(response);
      } catch (error) {
        console.error("Error fetching Unsplash photos:", error);
        toast({
          title: "Error fetching photos",
          description: "Failed to load photos. Please try again.",
          duration: 3000,
        });
      } finally {
        setIsSearching(false);
      }
    },
    [toast]
  );

  const handlePhotoSelection = useCallback((photo: UnsplashPhoto) => {
    setSelectedPhotos((prev) => {
      const isSelected = prev.includes(photo.url);

      if (isSelected) {
        return prev.filter((url) => url !== photo.url);
      } else if (prev.length < MAX_PHOTOS) {
        return [...prev, photo.url];
      }
      return prev;
    });
  }, []);

  const cleanUp = useCallback(() => {
    setUnsplashPhotos([]);
    setSearchQuery("");
    setIsOpen(false);
  }, []);

  const getAutoDescription = useCallback(async () => {
    if (!tripData) return;

    setIsGeneratingDescription(true);
    setDescription("");
    try {
      await AIService.streamDescription(
        tripData,
        (chunk) => {
          setDescription((prev) => prev + chunk);
        },
        (error) => {
          console.error("Error generating description:", error);
          toast({
            title: "Error generating description",
            description: "Please try again later.",
            duration: 4000,
          });
        }
      );
    } catch (error) {
      console.error("Error generating description:", error);
      toast({
        title: "Error generating description",
        description: "Please try again later.",
        duration: 4000,
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  }, [tripData, toast]);

  const handleOpen = useCallback(() => {
    if (anonymousUser) {
      setRateLimit(
        "Create your account to publish and share this trip.",
        tripData?.name
      );
      return;
    }
    setIsOpen(true);
  }, [anonymousUser, setRateLimit, tripData?.name]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        searchUnsplash(searchQuery);
      }
    },
    [searchQuery, searchUnsplash]
  );

  const handleRemoveTag = useCallback((indexToRemove: number) => {
    setTags((prevTags) => prevTags.filter((_, i) => i !== indexToRemove));
  }, []);

  const handleRemovePhoto = useCallback((urlToRemove: string) => {
    setSelectedPhotos((prev) => prev.filter((url) => url !== urlToRemove));
  }, []);

  const handlePrivacyChange = useCallback(
    (newPrivacy: "public" | "private" | "friends") => {
      setPrivacy(newPrivacy);
    },
    []
  );

  const selectedPhotosContent = useMemo(() => {
    if (selectedPhotos.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-3">
          <i className="fa-light fa-images text-neutral-400 text-base mb-1.5"></i>
          <p className="text-xs font-light text-neutral-500">
            Select up to 5 photos to showcase your trip
          </p>
          <p className="text-[10px] font-light text-neutral-400 mt-0.5">
            First photo will be your main cover image
          </p>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5 h-full overflow-x-auto no-scrollbar rounded-md">
        {selectedPhotos.map((url, index) => (
          <div
            key={url}
            className="relative group h-full aspect-video flex-shrink-0"
          >
            <img
              src={formatImageUrl(url, "medium")}
              alt={`Selected photo ${index + 1}`}
              className="h-full w-full object-cover rounded-md"
            />
            <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity duration-200 rounded-md" />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemovePhoto(url);
              }}
              className="absolute top-1 right-1 bg-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-neutral-100"
            >
              <i className="fa-light fa-xmark text-neutral-600 text-[10px]"></i>
            </button>
            <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 rounded w-4 h-4 flex items-center justify-center">
              <span className="text-[10px] font-medium text-white">
                {index + 1}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }, [selectedPhotos, handleRemovePhoto]);

  const unsplashPhotosContent = useMemo(() => {
    if (isSearching) {
      return (
        <div className="grid grid-cols-2 gap-1.5 p-1 pb-10 max-h-full">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="relative aspect-video rounded-md overflow-hidden animate-pulse bg-neutral-200"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-neutral-300/50 to-transparent shimmer" />
            </div>
          ))}
        </div>
      );
    }

    if (unsplashPhotos.length === 0) {
      return (
        <div className="w-full text-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 space-y-3">
          <i className="fa-light fa-search text-neutral-400 text-base"></i>
          <p className="text-xs font-light text-neutral-500">
            No photos found for your search
          </p>
        </div>
      );
    }

    return (
      <>
        {selectedPhotos.length > 0 && (
          <div className="w-1/3 z-20 absolute flex items-center justify-center bottom-2.5 left-1/2 -translate-x-1/2 bg-white rounded-full py-1.5 ring-1 ring-indigo-500 shadow-md">
            <p className="text-[10px] font-light text-neutral-600">
              {selectedPhotos.length} of {MAX_PHOTOS} photos selected
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-1.5 p-1 pb-10 max-h-full">
          {unsplashPhotos.map((photo: UnsplashPhoto) => (
            <div
              key={photo.url}
              className={`relative aspect-video rounded-md overflow-hidden cursor-pointer group 
              ${
                selectedPhotos.includes(photo.url)
                  ? "ring-1.5 ring-indigo-500"
                  : ""
              }
              ${
                selectedPhotos.length >= MAX_PHOTOS &&
                !selectedPhotos.includes(photo.url)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={() => handlePhotoSelection(photo)}
            >
              <LazyLoad
                src={formatImageUrl(photo.url, "medium")}
                lowResSrc={formatImageUrl(photo.url, "low")}
                alt={photo.alt || "unsplash photo"}
                className="object-cover w-full h-full"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200" />

              {/* Selection indicator */}
              {selectedPhotos.includes(photo.url) && (
                <div className="absolute top-1.5 right-1.5 bg-indigo-500 rounded-full w-5 h-5 flex items-center justify-center">
                  <i className="fa-solid fa-check text-[10px] text-white"></i>
                </div>
              )}

              {/* Selection number badge */}
              {selectedPhotos.includes(photo.url) && (
                <div className="absolute bottom-1.5 left-1.5 bg-black bg-opacity-50 rounded w-4 h-4 flex items-center justify-center">
                  <span className="text-[10px] font-medium text-white">
                    {selectedPhotos.indexOf(photo.url) + 1}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </>
    );
  }, [unsplashPhotos, selectedPhotos, isSearching, handlePhotoSelection]);

  const tagsContent = useMemo(() => {
    return (
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-neutral-200 focus-within:border-indigo-400 px-2.5 min-h-10 py-1.5 focus:outline-none">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="group inline-flex items-center gap-1 rounded-md bg-neutral-100 px-1.5 py-0.5 text-[12.5px] font-light text-neutral-700"
          >
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(index)}
              className="text-neutral-500 hover:text-neutral-700 transition-all duration-500 opacity-0 group-hover:opacity-100"
            >
              <i className="fa-light fa-xmark text-sm pl-0.5"></i>
            </button>
          </span>
        ))}
        <input
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagInput}
          placeholder={tags.length === 0 ? "Add tags" : ""}
          className="text-sm flex-1 border-0 bg-transparent py-1 px-0 font-light placeholder:text-foreground-muted focus:outline-none focus:ring-0"
        />
      </div>
    );
  }, [tags, tagInput, handleTagInput, handleRemoveTag]);

  if (!tripInfo) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) cleanUp();
      }}
    >
      <DialogTrigger
        asChild
        onClick={handleOpen}
        name="publish-trip"
        className="focus:ring-0 focus-visible:ring-transparent border-none focus:outline-none"
      >
        <button
          className="flex flex-row text-[13px] 2xl:text-[14px] space-x-1.5 items-center h-9 2xl:h-11 px-2.5 2xl:px-3 hover:opacity-90 bg-indigo-700 text-indigo-50 rounded-full focus:ring-0 focus-visible:ring-transparent transition-all duration-300 ease-in-out"
          data-tutorial="edit-trip"
        >
          <GlobeAmericasIcon className="w-4 h-4 2xl:w-5 2xl:h-5" />
          <p className="font-normal">Publish</p>
        </button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-5xl bg-white rounded-xl p-4 w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-neutral-800">
            Edit Trip Details
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col md:grid md:grid-cols-2 gap-x-6 gap-y-4 flex-1 overflow-hidden"
        >
          {/* Left Column - Text Inputs */}
          <div className="flex flex-col space-y-3 overflow-y-auto no-scrollbar pr-2 flex-1">
            <div className="space-y-1.5">
              <Label
                htmlFor="name"
                className="text-xs font-medium text-neutral-700"
              >
                Trip Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter trip name"
                autoFocus={false}
                className="text-sm rounded-lg font-light border-neutral-200 focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex flex-row items-center justify-between">
                <Label
                  htmlFor="description"
                  className="text-xs font-medium text-neutral-700"
                >
                  Description
                </Label>
                <button
                  type="button"
                  onClick={getAutoDescription}
                  disabled={isGeneratingDescription}
                  className="h-8 flex items-center gap-2 px-3 rounded-full hover:bg-indigo-50 transition-all ease-in-out duration-300 text-indigo-600 opacity-75 hover:opacity-100 hover:text-indigo-700"
                  title="Generate AI description"
                >
                  <span className="text-xs font-medium">
                    {isGeneratingDescription
                      ? "Generating with Tora"
                      : "Generate with Tora"}
                  </span>
                  {isGeneratingDescription ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <i className="fa-solid fa-sparkles text-sm"></i>
                  )}
                </button>
              </div>
              <div className="relative">
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your trip"
                  className="text-sm rounded-lg font-light min-h-[100px] h-full resize-none leading-relaxed no-scrollbar border-neutral-200 focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="tags"
                className="text-xs font-medium text-neutral-700"
              >
                Tags
              </Label>
              {tagsContent}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-neutral-700">
                Privacy
              </Label>
              <div className="flex flex-col space-y-1.5">
                <div className="grid grid-cols-3 gap-1.5 h-full min-h-[100px]">
                  <button
                    type="button"
                    className={`
                      flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer
                      border transition-all duration-300 ease-in-out h-full
                      ${
                        privacy === "private"
                          ? "bg-blue-50/80 border-blue-200"
                          : "border-neutral-200 bg-white"
                      }
                    `}
                    onClick={() => handlePrivacyChange("private")}
                  >
                    <div
                      className={`
                        p-1.5 rounded-lg mb-2 transition-colors duration-200
                        ${
                          privacy === "private"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-neutral-100 text-neutral-500"
                        }
                      `}
                    >
                      <LockClosedIcon className="w-4 h-4" />
                    </div>
                    <span
                      className={`
                        text-sm transition-all duration-200 ease-in-out text-center
                        ${
                          privacy === "private"
                            ? "text-neutral-800 font-medium"
                            : "text-neutral-700 font-light"
                        }
                      `}
                    >
                      Private
                    </span>
                    <span className="text-[10px] font-light text-neutral-500 text-center mt-0.5">
                      For you only
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`
                      flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer
                      border transition-all duration-300 ease-in-out h-full
                      ${
                        privacy === "friends"
                          ? "bg-blue-50/80 border-blue-200"
                          : "border-neutral-200 bg-white"
                      }
                    `}
                    onClick={() => handlePrivacyChange("friends")}
                  >
                    <div
                      className={`
                        p-1.5 rounded-lg mb-2 transition-colors duration-200
                        ${
                          privacy === "friends"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-neutral-100 text-neutral-500"
                        }
                      `}
                    >
                      <UsersIcon className="w-4 h-4" />
                    </div>
                    <span
                      className={`
                        text-sm transition-all duration-200 ease-in-out text-center
                        ${
                          privacy === "friends"
                            ? "text-neutral-800 font-medium"
                            : "text-neutral-700 font-light"
                        }
                      `}
                    >
                      Friends
                    </span>
                    <span className="text-[10px] font-light text-neutral-500 text-center mt-0.5">
                      For friends
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`
                      flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer
                      border transition-all duration-300 ease-in-out h-full
                      ${
                        privacy === "public"
                          ? "bg-blue-50/80 border-blue-200"
                          : "border-neutral-200 bg-white"
                      }
                    `}
                    onClick={() => handlePrivacyChange("public")}
                  >
                    <div
                      className={`
                        p-1.5 rounded-lg mb-2 transition-colors duration-200
                        ${
                          privacy === "public"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-neutral-100 text-neutral-500"
                        }
                      `}
                    >
                      <GlobeAmericasIcon className="w-4 h-4" />
                    </div>
                    <span
                      className={`
                        text-sm transition-all duration-200 ease-in-out text-center
                        ${
                          privacy === "public"
                            ? "text-neutral-800 font-medium"
                            : "text-neutral-700 font-light"
                        }
                      `}
                    >
                      Public
                    </span>
                    <span className="text-[10px] font-light text-neutral-500 text-center mt-0.5">
                      For everyone
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Image Management */}
          <div className="flex flex-col space-y-3 overflow-hidden flex-1 mt-4 md:mt-0">
            <div className="space-y-1.5 flex-shrink-0">
              <Label className="text-xs font-medium text-neutral-700">
                Cover Photos
              </Label>

              {/* Selected Photos Preview */}
              <div className="w-full aspect-[3/1] rounded-lg border border-neutral-200 bg-neutral-50 p-1.5">
                {selectedPhotosContent}
              </div>
            </div>

            <Tabs
              defaultValue="unsplash"
              className="w-full flex-1 flex flex-col overflow-hidden"
            >
              <TabsContent
                value="unsplash"
                className="flex-1 flex flex-col mt-0 data-[state=active]:flex-1 overflow-hidden"
              >
                <div className="flex flex-col space-y-3 h-full">
                  <div className="relative flex-shrink-0">
                    <img
                      src="https://waytora.com/cdn-cgi/image/quality=100/https://pub-b8cc892604024ad0a45e17686b510e8f.r2.dev/Unsplash_Symbol.png"
                      alt="Unsplash"
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3"
                    />
                    <Input
                      placeholder="Search Unsplash photos..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyDown}
                      className="text-sm px-8 rounded-lg font-light"
                    />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        searchUnsplash(searchQuery);
                      }}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2"
                    >
                      <i className="fa-light fa-search text-neutral-500 text-sm"></i>
                    </button>
                  </div>

                  <div className="flex-shrink-0 h-[200px] overflow-hidden">
                    <ScrollArea className="h-full border p-1.5 rounded-lg overflow-hidden">
                      {unsplashPhotosContent}
                    </ScrollArea>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Submit Button - Full Width */}
          <div className="col-span-1 md:col-span-2 flex justify-center mt-4">
            <Button
              type="submit"
              disabled={isSaving}
              className={`w-full max-w-[180px] h-9 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-full text-xs font-medium shadow-sm hover:shadow-md transition-all duration-200 ease-in-out flex-shrink-0 ${
                isSaving ? "opacity-80 cursor-not-allowed" : ""
              }`}
            >
              <div className="flex items-center justify-center space-x-1.5">
                {isSaving && (
                  <i className="fa-solid fa-circle-notch fa-spin text-xs"></i>
                )}
                <span className="inline-block">
                  {isSaving ? "Saving..." : "Save Trip"}
                </span>
              </div>
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
});

EditTripInfo.displayName = "EditTripInfo";

export default EditTripInfo;
